import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  assistantsApi,
  providerOptionsApi,
  toolsApi,
  voicesApi,
  type AssistantPayload,
  type AssistantToolPayload,
  type ProviderKind,
  type VoiceFilters,
} from "./api";

export function useAssistants() {
  return useQuery({ queryKey: ["assistants"], queryFn: assistantsApi.list });
}

/** The whole catalog once; filter per kind where needed. */
export function useProviderOptions(kind?: ProviderKind) {
  const q = useQuery({
    queryKey: ["assistant-provider-options"],
    queryFn: providerOptionsApi.list,
    staleTime: 5 * 60_000,
  });
  const options = kind ? (q.data ?? []).filter((o) => o.kind === kind) : q.data ?? [];
  return { ...q, options };
}

/** Live voices from the provider's own API — refetched on every mount so the
 * picker always shows the provider's current list (server caches ~5 min). */
export function useProviderVoices(provider?: string, filters: VoiceFilters = {}) {
  return useQuery({
    queryKey: ["assistant-voices", provider, filters],
    queryFn: () => voicesApi.list(provider!, filters),
    enabled: !!provider,
    staleTime: 0,
    refetchOnMount: "always",
    retry: false,
    // Keep the previous list on screen while a new search/filter loads.
    placeholderData: (prev) => prev,
  });
}

/** This assistant's call log (Logs tab); refetched on open so new Talk calls
 * show up without a manual refresh. */
export function useAssistantCalls(id: string, enabled = true) {
  return useQuery({
    queryKey: ["assistant-calls", id],
    queryFn: () => assistantsApi.calls(id),
    enabled,
    staleTime: 0,
    refetchOnMount: "always",
  });
}

/** Per-assistant stats for the Analysis tab; fresh on every open. */
export function useAssistantAnalysis(id: string, enabled = true) {
  return useQuery({
    queryKey: ["assistant-analysis", id],
    queryFn: () => assistantsApi.analysis(id),
    enabled,
    staleTime: 0,
    refetchOnMount: "always",
  });
}

/** PATCH an assistant and refresh the list (editor + sidebar read from it). */
export function useUpdateAssistant(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: AssistantPayload) => assistantsApi.update(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["assistants"] }),
    onError: () => toast.error("Couldn't save the assistant. Please try again."),
  });
}

/* ── Tools ──────────────────────────────────────────────────────────────── */

/** The tools attached to one assistant (Tools tab). */
export function useAssistantTools(id: string, enabled = true) {
  return useQuery({
    queryKey: ["assistant-tools", id],
    queryFn: () => toolsApi.list(id),
    enabled: enabled && !!id,
  });
}

/** Recent runs of one API Request tool — opened on demand from its card. */
export function useToolRuns(toolId: string, enabled = false) {
  return useQuery({
    queryKey: ["assistant-tool-runs", toolId],
    queryFn: () => toolsApi.runs(toolId),
    enabled: enabled && !!toolId,
    staleTime: 0,
    refetchOnMount: "always",
  });
}

/** DRF returns per-field errors; surface the first one rather than a generic
 * "couldn't save" that hides which field the backend rejected. */
/** The sentence explaining why a save failed.
 *
 * The API wraps failures as { error: { code, message, details } }, with the
 * per-field reasons inside `details` — reading the top level only ever found
 * the wrapper, so every rejection showed the generic fallback and the reason
 * was only visible in the server log.
 */
function toolError(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof data === "string") return data;
  if (!data || typeof data !== "object") return fallback;

  const wrapper = (data as {
    error?: { message?: string; details?: Record<string, unknown> };
  }).error;
  const fields = wrapper ? wrapper.details : (data as Record<string, unknown>);

  if (fields && typeof fields === "object") {
    const first = Object.values(fields)[0];
    if (Array.isArray(first) && typeof first[0] === "string") return first[0];
    if (typeof first === "string") return first;
  }
  // `message` on a validation failure is a stringified dict — no use to anyone.
  const message = wrapper?.message ?? "";
  return message && !message.startsWith("{") ? message : fallback;
}

export function useSaveTool(assistantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: AssistantToolPayload & { id?: string }) =>
      id ? toolsApi.update(id, payload) : toolsApi.create({ ...payload, assistant: assistantId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["assistant-tools", assistantId] }),
    onError: (error) => toast.error(toolError(error, "Couldn't save the tool.")),
  });
}

export function useDeleteTool(assistantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => toolsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["assistant-tools", assistantId] }),
    onError: (error) => toast.error(toolError(error, "Couldn't remove the tool.")),
  });
}
